/**
 * AttachmentPreviewDialog — in-app preview of an email attachment without
 * downloading it. Fetches a short-lived `inline`-disposition presigned URL and
 * renders images / PDFs in-page; other types fall back to a download prompt.
 */

import { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import {
  Loader2,
  Download,
  FolderInput,
  ExternalLink,
  CalendarClock,
  MapPin,
  CalendarPlus,
} from 'lucide-react';
import { api, type CalendarInvitePreview } from '@/lib/api';
import { toast } from '@/components/ui/use-toast';

export interface PreviewAttachment {
  attachmentId: string;
  filename: string;
  mimeType: string;
  /** Underlying Vault document id; present ⇒ the attachment can be saved to a folder. */
  documentId?: string | null;
}

const baseMime = (m: string) => m.toLowerCase().split(';')[0].trim();
const isImage = (m: string) => m.startsWith('image/');
// Senders / IMAP parsers label PDFs inconsistently (application/pdf, x-pdf,
// acrobat, or a generic octet-stream with a .pdf name). Detect by either, so the
// preview affordance reliably appears and the iframe gets the PDF branch.
const PDF_MIME = /^application\/(pdf|x-pdf|acrobat|vnd\.pdf)$/i;
const hasPdfExt = (name?: string) => !!name && /\.pdf$/i.test(name.trim());
const isPdf = (m: string, name?: string) => PDF_MIME.test(m) || hasPdfExt(name);
// A meeting invite (.ics). Rendered as an event CARD, never a raw-text iframe —
// browsers have no inline renderer for text/calendar, so the iframe shows blank.
const isCalendarInvite = (m: string, name?: string) =>
  baseMime(m) === 'text/calendar' || (!!name && /\.ics$/i.test(name.trim()));
// text/calendar is excluded here (handled by the invite card above), leaving the
// iframe text branch for content browsers actually render inline.
const isText = (m: string) => {
  const b = baseMime(m);
  if (b === 'text/calendar') return false;
  return b.startsWith('text/') || b === 'application/json' || b === 'application/xml';
};

/** Whether the in-app preview can render this attachment (image / PDF / text / invite). */
export function isPreviewableAttachment(mimeType: string, filename?: string): boolean {
  return (
    isImage(mimeType) ||
    isPdf(mimeType, filename) ||
    isText(mimeType) ||
    isCalendarInvite(mimeType, filename)
  );
}

/** Format an event's date range for the invite card (all-day → date only). */
function formatInviteWhen(startAt: string, endAt: string, isAllDay?: boolean): string {
  const start = new Date(startAt);
  const end = new Date(endAt);
  if (Number.isNaN(start.getTime())) return '';
  if (isAllDay) {
    const d = start.toLocaleDateString(undefined, { dateStyle: 'full' });
    return d;
  }
  const sameDay = start.toDateString() === end.toDateString();
  const startStr = start.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
  if (Number.isNaN(end.getTime())) return startStr;
  const endStr = sameDay
    ? end.toLocaleTimeString(undefined, { timeStyle: 'short' })
    : end.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
  return `${startStr} – ${endStr}`;
}

/** In-app card for a parsed `.ics` meeting invite (event details, not raw text). */
function CalendarInviteCard({ invite }: { invite: CalendarInvitePreview }) {
  if (invite.events.length === 0) {
    return (
      <div className="text-center text-sm text-muted-foreground">
        <CalendarClock className="mx-auto mb-2 h-6 w-6" />
        <p>This invite has no event details to show.</p>
        {invite.errors.length > 0 && (
          <p className="mt-1 text-xs text-destructive">{invite.errors[0]}</p>
        )}
      </div>
    );
  }
  return (
    <div className="w-full max-w-xl space-y-3 self-start">
      {invite.events.map((ev, i) => (
        <div key={i} className="rounded-lg border bg-card p-4 text-left">
          <div className="mb-2 flex items-start gap-2">
            <CalendarClock className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
            <div className="min-w-0">
              <div className="font-medium leading-snug">{ev.title || '(untitled event)'}</div>
              {ev.status === 'cancelled' && (
                <span className="text-xs font-medium text-destructive">Cancelled</span>
              )}
              {ev.status === 'tentative' && (
                <span className="text-xs font-medium text-muted-foreground">Tentative</span>
              )}
            </div>
          </div>
          <div className="text-sm text-muted-foreground">
            {formatInviteWhen(ev.startAt, ev.endAt, ev.isAllDay)}
            {ev.timezone ? ` (${ev.timezone})` : ''}
          </div>
          {ev.location && (
            <div className="mt-1 flex items-center gap-1.5 text-sm text-muted-foreground">
              <MapPin className="h-3.5 w-3.5 shrink-0" />
              <span className="truncate">{ev.location}</span>
            </div>
          )}
          {ev.description && (
            <p className="mt-2 line-clamp-4 whitespace-pre-wrap text-sm text-muted-foreground">
              {ev.description}
            </p>
          )}
        </div>
      ))}
      {invite.warnings.length > 0 && (
        <p className="text-xs text-muted-foreground">{invite.warnings[0]}</p>
      )}
    </div>
  );
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  messageId: string | null;
  attachment: PreviewAttachment | null;
  /** Save a copy to a Vault folder; shown only when the attachment is saveable. */
  onSaveToVault?: () => void;
  /** Add a `.ics` invite to the calendar; shown only for calendar invites. */
  onAddToCalendar?: () => void;
  /** Disables the "Add to calendar" action while a request is in flight. */
  addingToCalendar?: boolean;
}

export function AttachmentPreviewDialog({
  open,
  onOpenChange,
  messageId,
  attachment,
  onSaveToVault,
  onAddToCalendar,
  addingToCalendar,
}: Props) {
  const [url, setUrl] = useState<string | null>(null);
  const [invite, setInvite] = useState<CalendarInvitePreview | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const calendarInvite = attachment
    ? isCalendarInvite(attachment.mimeType, attachment.filename)
    : false;

  useEffect(() => {
    if (!open || !messageId || !attachment) {
      setUrl(null);
      setInvite(null);
      setError(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    setUrl(null);
    setInvite(null);
    // Meeting invites (.ics) are parsed server-side into event fields and shown
    // as a card — the raw iCalendar text can't render inline. A missing object
    // (e.g. bytes purged) surfaces as a graceful error here, not a blank pane.
    if (isCalendarInvite(attachment.mimeType, attachment.filename)) {
      api
        .getEmailCalendarInvitePreview(messageId, attachment.attachmentId)
        .then((res) => {
          if (!cancelled) setInvite(res);
        })
        .catch((e) => {
          if (!cancelled)
            setError(e instanceof Error ? e.message : 'Couldn’t load this meeting invite');
        })
        .finally(() => {
          if (!cancelled) setLoading(false);
        });
      return () => {
        cancelled = true;
      };
    }
    api
      .getEmailMailboxAttachmentDownloadUrl(messageId, attachment.attachmentId, 'inline')
      .then((res) => {
        if (!cancelled) setUrl(res.url);
      })
      .catch((e) => {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Failed to load preview');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, messageId, attachment]);

  const download = async () => {
    if (!messageId || !attachment) return;
    try {
      const { url: dl } = await api.getEmailMailboxAttachmentDownloadUrl(
        messageId,
        attachment.attachmentId,
      );
      const a = document.createElement('a');
      a.href = dl;
      a.download = attachment.filename;
      a.rel = 'noopener';
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch (e) {
      toast({
        title: 'Couldn’t download attachment',
        description: e instanceof Error ? e.message : 'Please try again.',
        variant: 'destructive',
      });
    }
  };

  // Open the attachment in a new browser tab via its short-lived inline URL —
  // the reliable cross-browser way to "open" a PDF when the in-iframe viewer is
  // unavailable (Firefox, blocked plugin, etc.). Fetches a fresh URL so it works
  // even if the inline preview above failed to load.
  const openInNewTab = async () => {
    if (!messageId || !attachment) return;
    try {
      const { url: inlineUrl } = await api.getEmailMailboxAttachmentDownloadUrl(
        messageId,
        attachment.attachmentId,
        'inline',
      );
      window.open(inlineUrl, '_blank', 'noopener,noreferrer');
    } catch (e) {
      toast({
        title: 'Couldn’t open attachment',
        description: e instanceof Error ? e.message : 'Please try again.',
        variant: 'destructive',
      });
    }
  };

  const mime = attachment?.mimeType ?? '';
  const pdf = isPdf(mime, attachment?.filename);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-4xl">
        <DialogHeader>
          <DialogTitle className="truncate pr-6">
            {attachment?.filename ?? 'Attachment'}
          </DialogTitle>
        </DialogHeader>
        <div className="flex max-h-[70vh] min-h-[50vh] items-center justify-center overflow-auto">
          {loading ? (
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          ) : error ? (
            <p className="text-sm text-destructive">{error}</p>
          ) : invite ? (
            <CalendarInviteCard invite={invite} />
          ) : url && isImage(mime) ? (
            <img
              src={url}
              alt={attachment?.filename ?? ''}
              className="max-h-[68vh] max-w-full object-contain"
            />
          ) : url && pdf ? (
            // Sandboxed as defense-in-depth: the server only ever serves an
            // inline, script-safe content-type here (html/svg are downgraded to a
            // download), and the browser's native PDF viewer still renders under
            // an empty sandbox. Falls back to the Download / Open-in-tab actions.
            <iframe
              src={url}
              title={attachment?.filename ?? 'PDF preview'}
              sandbox=""
              className="h-[68vh] w-full"
            />
          ) : url && isText(mime) ? (
            // Sandboxed so a text/html attachment can't run scripts on preview.
            <iframe
              src={url}
              title={attachment?.filename ?? 'Text preview'}
              sandbox=""
              className="h-[68vh] w-full bg-white"
            />
          ) : (
            <div className="text-center text-sm text-muted-foreground">
              <p>Preview isn’t available for this file type.</p>
              <p className="text-xs">{mime || 'unknown type'}</p>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Close
          </Button>
          {/* "Open in new tab" uses an inline URL, which is blank for a .ics —
              hide it for invites (the card + Add-to-calendar cover that case). */}
          {!calendarInvite && (
            <Button variant="outline" className="gap-1" onClick={openInNewTab}>
              <ExternalLink className="h-4 w-4" /> Open in new tab
            </Button>
          )}
          {calendarInvite && onAddToCalendar && (
            <Button
              variant="outline"
              className="gap-1"
              disabled={addingToCalendar}
              onClick={onAddToCalendar}
            >
              <CalendarPlus className="h-4 w-4" /> Add to calendar
            </Button>
          )}
          {onSaveToVault && attachment?.documentId && (
            <Button variant="outline" className="gap-1" onClick={onSaveToVault}>
              <FolderInput className="h-4 w-4" /> Save to Vault
            </Button>
          )}
          <Button className="gap-1" onClick={download}>
            <Download className="h-4 w-4" /> Download
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
